/**
 * ENVELOPE — the one sealed unit that ever leaves a peer, for storage AND
 * for transport alike. This is the V5 replacement for QuBit's `{ts, pub,
 * sig}` envelope, applied one level lower: instead of sealing a whole
 * key/value write, it seals one Yjs CRDT update (a binary diff produced by
 * a single local transaction).
 *
 * Why per-update, not per-Doc: a Y.Doc's value only exists as the MERGE of
 * many peers' contributions, so no single peer can ever meaningfully sign
 * "the document" - by the time a signature was computed, a concurrent edit
 * may already have merged in and invalidated it. An individual update has
 * no such problem: it is atomic, immutable, and produced by exactly one
 * peer, so it can be signed exactly like QuBit's own value used to be.
 *
 * Two independent crypto steps happen here, always in this order - sign
 * over the CIPHERTEXT, never the plaintext (same rule QuStore's own
 * `#seal()` follows, see packages/core/src/store.js):
 *   1. ENCRYPT the update bytes for every space member (ECDH envelope
 *      encryption via QuCrypto.encrypt - the same "one content key, wrapped
 *      per recipient" shape QuStore already uses for `encryptWith`).
 *   2. SIGN the ciphertext with the author's Ed25519 key.
 *
 * This is deliberately the ONLY place a Yjs update is ever encrypted for
 * transport/storage. Whether the plaintext *inside* that update was already
 * itself ciphertext (an 'atomic-encrypted' field write, see field.js) or a
 * genuinely plaintext CRDT text-op (a 'text' field edit) makes no
 * difference here - sealUpdate() cannot tell the two apart, and does not
 * need to: it is the transport/storage boundary, not the field boundary.
 * That is exactly the property this design wants: a relay or a durable
 * store only ever handles ONE kind of thing (a sealed envelope), never a
 * per-field-type special case.
 *
 * `notify` (optional, see `sealUpdate()`'s own param doc) is the ONE
 * deliberate exception to "the relay only ever sees ciphertext": a small,
 * caller-supplied `{topic, to?}` hint that travels UNENCRYPTED alongside
 * the envelope, specifically so a content-blind relay CAN still decide who
 * to Push-notify without ever decrypting the write itself (see
 * `@qu/space-transport`'s `relay.js`). It is still tamper-evident - folded
 * into the same signed `sigInput` as `iv`/`ct` below, so altering it
 * in-flight invalidates the signature exactly like altering the ciphertext
 * would - but it is NOT tamper-evident against the ORIGINAL SENDER lying
 * about their own write (e.g. tagging a boring message `topic: 'mention'`
 * to spam someone's push notifications). That is an accepted, documented
 * tradeoff, not an oversight: the alternative (the relay verifying a
 * notify claim against real content) is impossible without breaking the
 * relay's blindness, and a sender who wants to spam a member they're
 * already in a Space with has cheaper ways to do it than a fake `notify`
 * hint (they could just also write more, real, honestly-tagged messages).
 *
 * ENVELOPE v2 — TWO MODES, one signature rule ("sign exactly the bytes
 * that get transported, nothing more"):
 *   - `sealUpdate()` (`mode: 'encrypted'`) - everything above, unchanged.
 *   - `sealPublicUpdate()` (`mode: 'public'`) - for Kind-Schema fields
 *     declared `visibility: 'public'` (see kind-schema.js): the raw Yjs
 *     update travels as PLAINTEXT (`data`), signed directly (no `iv`/`ct`/
 *     `to`/`senderXPub` - there is no ciphertext, so nothing to encrypt
 *     for). Anyone - relay, other clients, total strangers - can verify
 *     authenticity without a decryption key, which is the whole point: a
 *     `~pub`-owned identity Node's `pub`/`epub` fields need to be
 *     discoverable by someone who has never been in a Space with the
 *     owner before (see kind-schema.js's own doc comment on why
 *     `visibility: 'public'` exists at all).
 * Both modes stamp `mode` onto the envelope so `verifyEnvelope()`/
 * `openUpdate()` below need to branch on exactly one thing to handle
 * either kind uniformly - no second code path anywhere else that has to
 * know which mode it's looking at.
 *
 * SNAPSHOT/COMPACTION (the `snapshot` flag, both `seal*()` functions'
 * last param): NOT a third envelope shape - a snapshot is sealed with the
 * exact same `sealUpdate()`/`sealPublicUpdate()` as any other write, the
 * ONLY difference being what bytes it carries (a Yjs FULL-STATE encoding,
 * see `Space.compactNode()` in space.js, instead of one incremental
 * update) and this one extra signed bit. `Y.applyUpdate()` neither knows
 * nor cares whether an update is incremental or full-state - applying
 * either to a doc that already has some/all of that history is always
 * safe (Yjs dedupes by per-client clock, silently ignoring anything
 * already integrated) - so `snapshot` changes nothing about how a RECEIVER
 * integrates the bytes. What it DOES change is what a STORAGE ADAPTER
 * does with it: ordinarily a write is `storage.append()`ed onto a Node's
 * unbounded envelope log; a `snapshot: true` envelope is instead
 * `storage.replace()`d - the ENTIRE prior log for that Node is discarded
 * in favor of this one envelope, which by construction reconstructs the
 * exact same current state a fresh reader would converge to anyway (see
 * `@qu/space-storage`'s three adapters, and `@qu/space-transport`'s
 * relay.js `handleWrite()`). This is the actual fix for the real,
 * previously-documented gap in this framework's deletion story: Yjs
 * itself already garbage-collects a deleted item's CONTENT from a live,
 * `gc: true` `Y.Doc`'s own memory (the default for every `Y.Doc` in this
 * codebase) - but every sealed envelope that ever carried that deleted
 * content remains sitting in storage/a relay's mirror FOREVER until
 * something produces and propagates a compaction snapshot past it.
 *
 * Because `verifyEnvelope()` runs unchanged for a snapshot (same
 * `isAuthorizedWriter` check as any other write - see kind-schema.js's
 * ACL modes), authorizing WHO may compact a Node needs no new mechanism:
 * exactly whoever may already write to a Node may also snapshot it - a
 * `'members'`-mode Kind, any member; `'owner'`/`'named'`, the owner (or a
 * named grantee). This does subtly change what a signature MEANS for that
 * one envelope, worth being explicit about: an ordinary write's signature
 * says "I produced this specific change"; a snapshot's says "I attest
 * this is a faithful merge of everything up to now" - a real, accepted
 * shift in what's being claimed, not a bug.
 */
import { QuCrypto } from '@qu/core';

/**
 * @param {Uint8Array} update - Raw bytes from `doc.on('update', ...)`.
 * @param {object} sender - `{signingKey, signingPub, xPrivateKey, xPublicKey}` (Ed25519 + X25519 pairs).
 * @param {Array<Uint8Array>} recipientXPubKeys - Every space member's X25519 public key (encryption recipients).
 * @param {{topic: string, to?: string[]}|null} [notify] - Optional, UNENCRYPTED routing hint for
 *   push delivery (see this file's own doc comment). `topic` is a plain
 *   string (the calling field/Node validates it against the Kind-Schema's
 *   `notifyTopics` allowlist BEFORE calling this - see field.js - envelope.js
 *   itself has no Kind-Schema to check against). `to` (optional) is a list
 *   of base64 Ed25519 pubkeys narrowing which members this concerns (e.g.
 *   the @mentioned ones); omitted/empty means "every other space member."
 * @param {boolean} [snapshot=false] - see this file's own "SNAPSHOT/COMPACTION" doc comment.
 * @param {Array<Uint8Array>} [paddingXPubKeys] - OPTIONAL, default none (unchanged behavior):
 *   passed straight through to `QuCrypto.encrypt()`'s own `paddingXPubKeys` param - see that
 *   method's own doc comment. `Space`'s pluggable `sealStrategy` (`seal-strategies.js`,
 *   docs/routing-anonymity.md) is what computes this in practice - this function itself has no
 *   opinion on WHICH pubkeys get padded, only that it wires the param through.
 * @returns {Promise<object>} A plain, structurally-cloneable envelope - safe to hand to a Transport or a Storage adapter as-is.
 */
export async function sealUpdate(update, sender, recipientXPubKeys, notify = null, snapshot = false, paddingXPubKeys = []) {
  const { iv, ct, to } = await QuCrypto.encrypt(update, recipientXPubKeys, sender.xPrivateKey, paddingXPubKeys);
  const notifyBytes = encodeNotify(notify);
  const snapshotBytes = encodeSnapshotFlag(snapshot);
  const sigInput = concatBytes(concatBytes(concatBytes(iv, ct), notifyBytes), snapshotBytes);
  const sig = await QuCrypto.sign(sigInput, sender.signingKey);
  return {
    mode: 'encrypted',
    iv,
    ct,
    to, // [{pub: X25519 recipient pubkey, key: wrapped content key}]
    senderXPub: sender.xPublicKey,
    sig,
    pub: sender.signingPub, // Ed25519 - who to verify the signature against
    ts: Date.now(),
    ...(notify ? { notify } : {}), // present only when the write actually attached one - see this file's own doc comment.
    ...(snapshot ? { snapshot: true } : {}), // present only for a compaction snapshot - see this file's own "SNAPSHOT/COMPACTION" doc comment.
  };
}

/**
 * The `mode: 'public'` counterpart to `sealUpdate()` - see this file's own
 * "ENVELOPE v2" doc comment. No `recipientXPubKeys` parameter: there is no
 * encryption step, so no one to wrap a content key for.
 * @param {Uint8Array} update - Raw bytes from `doc.on('update', ...)`.
 * @param {object} sender - `{signingKey, signingPub}` (only the Ed25519 half is needed - no X25519 encryption happens here).
 * @param {{topic: string, to?: string[]}|null} [notify]
 * @param {boolean} [snapshot=false] - see this file's own "SNAPSHOT/COMPACTION" doc comment.
 * @returns {Promise<object>}
 */
export async function sealPublicUpdate(update, sender, notify = null, snapshot = false) {
  const notifyBytes = encodeNotify(notify);
  const snapshotBytes = encodeSnapshotFlag(snapshot);
  const sigInput = concatBytes(concatBytes(update, notifyBytes), snapshotBytes);
  const sig = await QuCrypto.sign(sigInput, sender.signingKey);
  return {
    mode: 'public',
    data: update, // plaintext - see this file's own doc comment.
    sig,
    pub: sender.signingPub,
    ts: Date.now(),
    ...(notify ? { notify } : {}),
    ...(snapshot ? { snapshot: true } : {}),
  };
}

/**
 * Verifies an envelope's write signature (including its `notify` hint, if
 * any - see this file's own doc comment), for EITHER mode. Requires ONLY
 * the signer's public key - never a decryption key, even for an
 * `encrypted`-mode envelope. A relay (see @qu/space-transport's
 * RelayForwarder) calls exactly this and nothing else, which is what makes
 * it structurally unable to read `encrypted`-mode content: it never even
 * receives an X25519 private key to attempt decryption with. (A
 * `public`-mode envelope has no such protection by design - see this
 * file's own doc comment - `verifyEnvelope()` returning `true` for one
 * means exactly "authentic," never "confidential.")
 * @param {object} envelope
 * @param {(pubBase64: string) => boolean|Promise<boolean>} isAuthorizedWriter - Kind-Schema's
 *   write-ACL check. May be sync (`'members'` mode - a flat Set lookup) or async (`'owner'`/
 *   `'named'` mode - `deriveOwnerNodeId()` involves a SHA-256 digest, see kind-schema.js) - always
 *   `await`ed here so callers never need to care which.
 * @returns {Promise<boolean>}
 */
export async function verifyEnvelope(envelope, isAuthorizedWriter) {
  const pubB64 = QuCrypto.toBase64(envelope.pub);
  if (!(await isAuthorizedWriter(pubB64))) return false;
  const notifyBytes = encodeNotify(envelope.notify ?? null);
  const snapshotBytes = encodeSnapshotFlag(envelope.snapshot === true);
  const sigInput =
    envelope.mode === 'public'
      ? concatBytes(concatBytes(envelope.data, notifyBytes), snapshotBytes)
      : concatBytes(concatBytes(concatBytes(envelope.iv, envelope.ct), notifyBytes), snapshotBytes);
  return QuCrypto.verify(sigInput, envelope.sig, envelope.pub);
}

/** A single tamper-evident byte marking a `snapshot` envelope (see this file's own "SNAPSHOT/COMPACTION" doc comment) - `false`/absent encodes to zero bytes, same "signs identically to before this feature existed" property `encodeNotify()` below has. Deliberately part of the SIGNED bytes, not a bare unsigned flag on the outer `{nodeId, envelope}` write message: a relay (or anyone else forwarding it) must not be able to make an ordinary incremental update look like a full-state snapshot (which a storage adapter treats as license to DISCARD everything older for that Node - see relay.js's own handling) without an authorized writer's actual signature over that exact claim. */
function encodeSnapshotFlag(snapshot) {
  return snapshot ? new Uint8Array([1]) : new Uint8Array(0);
}

/** Canonical (stable key order), UTF-8 encoding of a `notify` hint for the signature - `null`/absent encodes to zero bytes, so an envelope with no hint signs identically to before this feature existed. */
function encodeNotify(notify) {
  if (!notify) return new Uint8Array(0);
  const canonical = { topic: notify.topic, to: notify.to ?? [] };
  return new TextEncoder().encode(JSON.stringify(canonical));
}

/**
 * Returns the raw update bytes for `Y.applyUpdate()`, for an
 * already-signature-verified envelope of EITHER mode - the one place
 * calling code asks "give me the bytes" without needing to know or care
 * which mode produced them.
 *
 * `encrypted` mode: only a peer holding the matching X25519 private key
 * (i.e. an actual space member) can do this - a relay never can, by
 * construction (see verifyEnvelope's doc comment above). Throws just as
 * routinely for a PADDING entry (`@qu/core`'s `QuCrypto.encrypt()`'s own
 * `paddingXPubKeys` param, `Space`'s `sealStrategy`, docs/routing-
 * anonymity.md) - a member listed in `to` purely to hide the real
 * recipient set's size gets a structurally-identical but genuinely random
 * blob, which fails AES-GCM authentication exactly like any other
 * undecryptable entry - this function cannot tell "not a real recipient"
 * apart from "deliberately a padding target," by design: neither should
 * be distinguishable from the other to whoever's asking.
 * `public` mode: no decryption key needed or checked - `envelope.data` IS
 * the plaintext, for anyone, by design (see this file's own doc comment).
 * @param {object} envelope
 * @param {{xPrivateKey: Uint8Array, xPublicKey: Uint8Array}} [recipient] - Required for `encrypted` mode, ignored for `public`.
 * @returns {Promise<Uint8Array>}
 * @throws if `mode: 'encrypted'` and `recipient` is not among the envelope's intended recipients.
 */
export async function openUpdate(envelope, recipient) {
  if (envelope.mode === 'public') return envelope.data;
  const myPubB64 = QuCrypto.toBase64(recipient.xPublicKey);
  const entry = envelope.to.find((t) => QuCrypto.toBase64(t.pub) === myPubB64);
  if (!entry) throw new Error('openUpdate: recipient is not an intended reader of this envelope');
  return QuCrypto.decrypt(envelope.iv, envelope.ct, entry.key, envelope.senderXPub, recipient.xPrivateKey);
}

function concatBytes(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}
